import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  TablePagination,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Timeline as TimelineIcon,
  DriveFileRenameOutlineOutlined as DriveFileRenameIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../auth";
import ListLayout from "../../../components/Layout/ListLayout";
import {
  findSignatureBatchById,
  useListCoversToSign,
} from "../../../api/documentos/endpoints";
import {
  EnvelopeFilters,
  EnvelopeFiltersData,
} from "./components/EnvelopeFilters";
import { EnvelopeCards } from "./components/EnvelopeCards";
import { CoverDto } from "../../../api/documentos/model/coverDto";
import { ListCoversToSignParams } from "../../../api/documentos/model/listCoversToSignParams";
import React from "react";
import { getEnv } from "../../../utils/env";

const SIGH_APP_URL =
  getEnv("VITE_APP_SMART_SIGN_APP") ||
  "https://signer-sosdocs.hml.smartdocs.com.br";

const signSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  title: z.string().optional(),
  createdAt: z.string().optional(),
  signatureDueDate: z.string().optional(),
});

export const Route = createFileRoute("/__auth/signatures/sign")({
  component: SignListPage,
  validateSearch: (search) => signSearchSchema.parse(search),
  staticData: {
    title: "routes.signDocuments",
  },
});

function SignActionsCell({ row }: { row: CoverDto }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const tenant = user?.tenant || "default-domain";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingAction, setLoadingAction] = useState<"sign" | "history" | null>(
    null,
  );
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExecuteAction = async (actionType: "sign" | "history") => {
    handleClose();
    let hash = row.hash;

    if (!hash && actionType === "history") {
      setLoadingAction(actionType);
      try {
        const res = await findSignatureBatchById(tenant, row.id!);
        hash = (res as any)?.hash || (res as any)?.data?.hash;
      } catch (error) {
        console.error("Error fetching envelope hash:", error);
      } finally {
        setLoadingAction(null);
      }
    }

    if (hash) {
      const path = actionType === "sign" ? "link" : "validate-envelope";
      const url = `${SIGH_APP_URL}/${path}/${actionType === "history" ? hash : row.linkControlCode}`;
      window.open(url, "_blank");
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        disabled={loadingAction !== null}
      >
        {loadingAction ? (
          <Box
            sx={{
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ...
          </Box>
        ) : (
          <MoreVertIcon fontSize="small" />
        )}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => handleExecuteAction("sign")}
          disabled={loadingAction !== null}
        >
          <ListItemIcon>
            <DriveFileRenameIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("signatures.signDocs")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleExecuteAction("history")}
          disabled={loadingAction !== null}
        >
          <ListItemIcon>
            <TimelineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("signatures.history")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

function SignMobileActions({
  envelope,
  closeMenu,
  tenant,
}: {
  envelope: CoverDto;
  closeMenu: () => void;
  tenant: string;
}) {
  const [loadingAction, setLoadingAction] = useState<"sign" | "history" | null>(
    null,
  );
  const { t } = useTranslation();

  const handleExecuteAction = async (actionType: "sign" | "history") => {
    closeMenu();
    let hash = envelope.hash;

    if (!hash && actionType === "history") {
      setLoadingAction(actionType);
      try {
        const res = await findSignatureBatchById(tenant, envelope.id!);
        hash = (res as any)?.hash || (res as any)?.data?.hash;
      } catch (error) {
        console.error("Error fetching envelope hash:", error);
      } finally {
        setLoadingAction(null);
      }
    }

    if (hash) {
      const path = actionType === "sign" ? "link" : "validate-envelope";
      const url = `${SIGH_APP_URL}/${path}/${actionType === "history" ? hash : envelope.linkControlCode}`;
      window.open(url, "_blank");
    }
  };

  return (
    <>
      <MenuItem
        onClick={() => handleExecuteAction("sign")}
        disabled={loadingAction !== null}
      >
        <ListItemIcon>
          {loadingAction === "sign" ? (
            <Box
              sx={{
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ...
            </Box>
          ) : (
            <DriveFileRenameIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText>{t("signatures.signDocs")}</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => handleExecuteAction("history")}
        disabled={loadingAction !== null}
      >
        <ListItemIcon>
          {loadingAction === "history" ? (
            <Box
              sx={{
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ...
            </Box>
          ) : (
            <TimelineIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText>{t("signatures.history")}</ListItemText>
      </MenuItem>
    </>
  );
}

function SignListPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const tenant = user?.tenant || "default-domain";

  const {
    data: coversDataResponse,
    isLoading,
    error,
  } = useListCoversToSign(tenant, {
    page: search.page - 1,
    size: search.size,
    sort: ["id,asc"],
    title: search.title || undefined,
    createdAt: search.createdAt || undefined,
    signatureDueDate: search.signatureDueDate || undefined,
  } as unknown as ListCoversToSignParams);

  const data = coversDataResponse as any;

  const handleFilter = (filters: EnvelopeFiltersData) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...filters,
        page: 1,
      }),
    });
  };

  const handleClearFilters = () => {
    navigate({
      search: (prev) => ({
        page: 1,
        size: prev.size,
      }),
    });
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: newPage + 1,
      }),
    });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    navigate({
      search: (prev) => ({
        ...prev,
        size: parseInt(event.target.value, 10),
        page: 1,
      }),
    });
  };

  const columnHelper = createColumnHelper<CoverDto>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("title", {
        header: t("signatures.envelopeTitle"),
        cell: (info) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {info.getValue() || "-"}
            </Typography>
          </Box>
        ),
      }),
      columnHelper.accessor("authorName", {
        header: t("signatures.createdBy"),
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("createdAt", {
        header: t("common.creationDate"),
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue() as string).toLocaleString("pt-BR", {
                dateStyle: "short",
                timeStyle: "short",
              })
            : "-",
      }),
      columnHelper.accessor("signatureDueDate", {
        header: t("signatures.expirationDate"),
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue() as string).toLocaleDateString("pt-BR")
            : "-",
      }),
      columnHelper.accessor("status", {
        header: t("signatures.situation"),
        cell: (info) => {
          const status = info.getValue() as string;
          let label = t("signatures.status.pending");
          let color: "default" | "success" | "error" | "warning" | "info" =
            "default";

          switch (status) {
            case "COMPLETED":
            case "SIGNED":
              label = t("signatures.status.signed");
              color = "success";
              break;
            case "SIGNATURE_STARTED":
            case "PROCESSING":
              label = t("signatures.status.inProgress");
              color = "info";
              break;
            case "CANCELED":
            case "CANCELLED":
              label = t("signatures.status.canceled");
              color = "error";
              break;
            case "REJECTED":
              label = t("signatures.status.rejected");
              color = "error";
              break;
            case "PENDING":
            default:
              label = t("signatures.status.pending");
              color = "warning";
              break;
          }

          return <Chip label={label} color={color} size="small" />;
        },
      }),
      columnHelper.display({
        id: "actions",
        header: t("signatures.actions"),
        cell: (info) => <SignActionsCell row={info.row.original} />,
        meta: { align: "right", isSticky: true },
      }),
    ],
    [columnHelper, t],
  );

  const table = useReactTable({
    data: data?.content || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: data?.totalPages || -1,
  });

  return (
    <ListLayout
      title={t("signatures.signDocuments")}
      subtitle={t("signatures.pendingSignature")}
      isLoading={isLoading}
      error={error}
      isEmpty={!data?.content?.length}
      filters={
        <EnvelopeFilters
          onFilter={handleFilter}
          onClear={handleClearFilters}
          isLoading={isLoading}
          initialValues={search}
          hideStatus={true}
        />
      }
    >
      {isMobile ? (
        <EnvelopeCards
          envelopes={data?.content || []}
          renderActions={(envelope, closeMenu) => (
            <SignMobileActions
              envelope={envelope}
              closeMenu={closeMenu}
              tenant={tenant}
            />
          )}
        />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "background.default" }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta: any = header.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={header.id}
                        sx={{
                          fontWeight: 600,
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "background.default",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta: any = cell.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={cell.id}
                        sx={{
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "inherit",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={data?.totalElements || 0}
        page={(search.page || 1) - 1}
        onPageChange={handlePageChange}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={handleRowsPerPageChange}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t("common.of")} ${count !== -1 ? count : `${t("common.moreThan")} ${to}`}`
        }
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      />
    </ListLayout>
  );
}
